// core/services/usersService.ts — async service for the users registry.
//
// Components must NOT import this service or the data layer directly.
// Use hooks/useUser.ts instead.

import type { User, SuggestedProfile, ProfileOverrides } from '@/core/models';
import type { MutedAccount, TopicsPayload } from '@/core/models/account';
import type { Post } from '@/core/models/post';
import { registerUser, allUsers } from '@/core/data/users';
import { api } from '@/core/lib/apiClient';
import { useMuteStore } from '@/core/stores/muteStore';
import { usePostStore } from '@/core/stores/postStore';
import { normHandle } from '@/core/stores/followStore';

/** One person in a followers / following list. */
export interface ConnectionRow {
  handle: string;
  name: string;
  avatarKey: string;
  verified: boolean;
  bio: string;
  /** Whether the VIEWER follows this person. */
  following: boolean;
  /** True for the viewer's own row (no Follow button). */
  you: boolean;
}

export const usersService = {
  /**
   * One page of who follows `handle` (`followers`) or who they follow (`following`).
   * Throws ApiError `private` (403) for a private account the viewer does not follow.
   */
  async connections(
    handle: string,
    kind: 'followers' | 'following',
    offset = 0,
  ): Promise<{ results: ConnectionRow[]; next: number | null }> {
    return api(`/users/${normHandle(handle)}/connections/?kind=${kind}&offset=${offset}&limit=30`);
  },

  /** Returns the User record by id (username) from the API, or null. */
  async getUser(id: string): Promise<User | null> {
    try {
      return await api<User>(`/users/${id}/`);
    } catch {
      return null;
    }
  },

  /** Returns the User record by handle from the API (leading @ stripped), or null. */
  async getByHandle(handle: string): Promise<User | null> {
    try {
      return await api<User>(`/users/${handle.replace(/^@/, '')}/`);
    } catch {
      return null;
    }
  },

  /** Adds or replaces a record in the registry. */
  registerUser(record: User): void {
    registerUser(record);
  },

  /**
   * Returns this user's own posts (newest-first) from the API, or [] on error.
   * Backed by a real per-user endpoint so the profile Posts/Media tabs don't
   * depend on the global feed being hydrated (it only hydrates on Home).
   */
  async getUserPosts(id: string): Promise<Post[]> {
    try {
      return await api<Post[]>(`/users/${id.replace(/^@/, '')}/posts/`);
    } catch {
      return [];
    }
  },

  /**
   * Returns the suggested profiles for the Who-to-Follow rail (from the API).
   *
   * 1.24.00: optional `{limit, for}` — `for: 'onboarding'` switches the server
   * ranking to interest match → region match → followers, and `limit` (1..20,
   * server-clamped) asks for more than the rail's default 5. With no options
   * the wire is byte-identical to before (`/users/suggested/`), so every
   * existing caller and its tests are unchanged.
   */
  async suggested(opts: { limit?: number; for?: 'onboarding' } = {}): Promise<SuggestedProfile[]> {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.for) params.set('for', opts.for);
    const qs = params.toString();
    return api<SuggestedProfile[]>(`/users/suggested/${qs ? `?${qs}` : ''}`);
  },

  /**
   * `GET /users/topics/` — the 8 topic keys + labels and the onboarding
   * minimum, from the server's single source of truth (content.topics.TOPICS).
   * Public. Callers fall back to core/data/feedTopics.ts on failure.
   */
  async topics(): Promise<TopicsPayload> {
    return api<TopicsPayload>('/users/topics/');
  },

  /** Returns whether the current user has muted this handle, and hydrates the store. */
  async isMuted(handle: string): Promise<boolean> {
    const r = await api<{ muted: boolean }>(`/users/${handle.replace(/^@/, '')}/muted/`);
    useMuteStore.getState().setMuted(handle, r.muted);
    return r.muted;
  },

  /**
   * Replace the mute set with the server's truth. Call at boot alongside
   * followsService.hydrate() (see useGlobalHydration) — without this, the ···
   * menu had no way to know a handle was already muted after a reload, so it
   * always showed "Mute @x" and a tap on an already-muted account UNMUTED them.
   */
  async hydrateMutes(): Promise<void> {
    const r = await api<{ handles: string[] }>('/users/me/muted/');
    const map: Record<string, boolean> = {};
    for (const h of r.handles) map[normHandle(h)] = true;
    useMuteStore.getState().setMutes(map);
  },

  /**
   * Toggle mute state for a handle. Muted authors' posts drop out of the home feed —
   * both server-side (future fetches) and immediately client-side (existing posts by
   * that author are removed from the store on a fresh mute; unmuting does not restore
   * them until the next fetch, matching hide's accepted behavior).
   * Returns null (without throwing) on failure so the caller can show a toast — mirrors
   * postsService.hidePost's error contract.
   */
  async toggleMute(handle: string): Promise<boolean | null> {
    try {
      const r = await api<{ handle: string; muted: boolean }>(
        `/users/${handle.replace(/^@/, '')}/mute/`,
        { method: 'POST' },
      );
      useMuteStore.getState().setMuted(handle, r.muted);
      if (r.muted) {
        const key = normHandle(handle);
        usePostStore.getState().removePostsByAuthorHandle(key);
      }
      return r.muted;
    } catch {
      return null;
    }
  },

  /**
   * Every account the signed-in user has muted, with enough profile to render
   * a manage-list row. Separate from `hydrateMutes()` because that endpoint
   * returns bare handles (the boot-hydration shape muteStore depends on),
   * which cannot render a list with names and avatars — mirrors
   * followsService.followedCreators()'s split from hydrate() for the same
   * reason.
   */
  async mutedAccounts(): Promise<MutedAccount[]> {
    const r = await api<{ muted: MutedAccount[] }>('/users/me/muted/details/');
    return r.muted ?? [];
  },

  /** Returns all user records currently in the registry (local cache). */
  async all(): Promise<User[]> {
    return allUsers();
  },

  /**
   * Apply ProfileOverrides onto the live registry record in-place.
   * Called by authService.updateProfile; also exported for other callers.
   */
  applyProfileOverrides(rec: User, profile: ProfileOverrides): void {
    if (profile.displayName !== undefined) {
      rec.name = profile.displayName;
      rec.avatarInitial = (profile.displayName[0] ?? '?').toUpperCase();
    }
    if (profile.bio !== undefined) rec.bio = profile.bio;
    if (profile.location !== undefined) rec.location = profile.location;
    if (profile.link !== undefined) rec.link = profile.link ?? null;
    if (profile.avatarKey !== undefined) rec.avatarKey = profile.avatarKey;
    if (profile.bannerTone !== undefined) rec.bannerTone = profile.bannerTone;
    if (profile.likesPublic !== undefined) rec.likesPublic = profile.likesPublic;
  },
} as const;
